<template>
  <div class="ep-page max-w-4xl">

    <!-- ── Loading ────────────────────────────────────────── -->
    <div v-if="loading" class="py-20"><div class="ep-spinner"></div></div>

    <!-- ── Error ──────────────────────────────────────────── -->
    <div v-else-if="error"
         class="rounded-2xl px-5 py-4"
         style="background: rgba(248,113,113,0.08); border: 1px solid rgba(248,113,113,0.25)">
      <p class="text-sm text-red-300">{{ error }}</p>
      <button @click="$router.push('/employee/payslips')"
              class="mt-2 text-xs font-medium text-red-300 hover:text-red-200 underline transition-colors">
        {{ t('empPayslipDetail.backToPayslips') }}
      </button>
    </div>

    <!-- ── Payslip ────────────────────────────────────────── -->
    <template v-else-if="payslip">
      <!-- Header -->
      <div class="ep-card p-5 sm:p-6">
        <button @click="$router.push('/employee/payslips')"
                class="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-indigo-300 transition-colors mb-4">
          <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
          {{ t('empPayslipDetail.backToPayslips') }}
        </button>

        <div class="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div class="min-w-0">
            <div class="flex items-center gap-3 flex-wrap">
              <h1 class="text-xl sm:text-2xl font-bold text-slate-100">
                {{ getMonthName(payslip.period.month) }} {{ payslip.period.year }}
              </h1>
              <span :class="getStatusClass(payslip.status)">
                <span class="ep-dot"></span>{{ getStatusText(payslip.status) }}
              </span>
            </div>
            <p class="mt-1.5 text-sm text-slate-400">
              {{ payslip.employee.name }} <span class="text-slate-600">({{ payslip.employee.code }})</span>
            </p>
            <p class="text-xs text-slate-500">{{ payslip.employee.department }} · {{ payslip.employee.position }}</p>
            <p class="mt-1 text-xs text-slate-600">
              Period: {{ formatDate(payslip.period.startDate) }} – {{ formatDate(payslip.period.endDate) }}
            </p>
          </div>

          <div class="flex gap-2 shrink-0">
            <button @click="downloadReceipt" :disabled="isDownloadingReceipt" class="ep-btn ep-btn-secondary ep-btn-sm">
              <svg v-if="isDownloadingReceipt" class="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
              </svg>
              <svg v-else class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              {{ isDownloadingReceipt ? t('empPayslipDetail.generating') : t('empPayslipDetail.receipt') }}
            </button>
            <button @click="downloadPDF" :disabled="isDownloadingPDF" class="ep-btn ep-btn-primary ep-btn-sm">
              <svg v-if="isDownloadingPDF" class="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
              </svg>
              <svg v-else class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              {{ isDownloadingPDF ? t('empPayslipDetail.generating') : t('empPayslipDetail.downloadPDF') }}
            </button>
          </div>
        </div>
      </div>

      <!-- Summary band -->
      <div class="rounded-2xl p-5 sm:p-6 relative overflow-hidden"
           style="background: linear-gradient(135deg, rgba(99,102,241,0.18) 0%, rgba(124,58,237,0.14) 100%); border: 1px solid rgba(129,140,248,0.3)">
        <div class="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
          <div>
            <p class="text-xs font-medium uppercase tracking-wide text-indigo-300/70">{{ t('empPayslipDetail.grossPay') }}</p>
            <p class="text-2xl font-bold text-slate-100 tabular-nums mt-1">{{ formatCurrency(payslip.earnings.grossPay) }}</p>
          </div>
          <div>
            <p class="text-xs font-medium uppercase tracking-wide text-indigo-300/70">{{ t('empPayslipDetail.totalDeductions') }}</p>
            <p class="text-2xl font-bold text-red-300 tabular-nums mt-1">−{{ formatCurrency(payslip.deductions.totalDeductions) }}</p>
          </div>
          <div>
            <p class="text-xs font-medium uppercase tracking-wide text-indigo-300/70">{{ t('empPayslipDetail.netPay') }}</p>
            <p class="text-3xl font-bold text-white tabular-nums mt-1">{{ formatCurrency(payslip.summary.netPay) }}</p>
          </div>
        </div>
      </div>

      <!-- Earnings -->
      <div class="ep-card p-5 sm:p-6">
        <h2 class="ep-section-title mb-4">
          <span class="ep-stat-icon ep-stat-emerald !w-8 !h-8 !rounded-lg">
            <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </span>
          {{ t('empPayslipDetail.earnings') }}
        </h2>

        <div class="space-y-3">
          <div class="flex justify-between items-center py-2.5" style="border-bottom: 1px solid rgba(255,255,255,0.06)">
            <span class="text-sm text-slate-400">{{ t('empPayslipDetail.baseSalary') }}</span>
            <span class="text-base font-semibold text-emerald-400 tabular-nums">{{ formatCurrency(payslip.earnings.baseSalary) }}</span>
          </div>
          <div class="flex justify-between items-center text-xs text-slate-500 pb-2.5" style="border-bottom: 1px solid rgba(255,255,255,0.06)">
            <span>{{ t('empPayslipDetail.workingHoursPerMonth') }} {{ payslip.earnings.workingHoursPerMonth }}</span>
            <span>{{ t('empPayslipDetail.hourlyRate') }} {{ formatCurrency(payslip.earnings.hourlyRate) }}/{{ t('empPayslipDetail.hr') }}</span>
          </div>

          <!-- Overtime -->
          <div v-if="payslip.earnings.overtimePay > 0"
               class="rounded-xl p-4"
               style="background: rgba(52,211,153,0.06); border: 1px solid rgba(52,211,153,0.22)">
            <div class="flex justify-between items-center">
              <div class="flex-1">
                <div class="flex items-center justify-between">
                  <span class="text-sm font-medium text-slate-200">{{ t('empPayslipDetail.overtimePay') }}</span>
                  <span class="text-base font-semibold text-emerald-400 tabular-nums">{{ formatCurrency(payslip.earnings.overtimePay) }}</span>
                </div>
                <p class="text-xs text-slate-500 mt-1">{{ payslip.earnings.overtimeHours }} {{ t('empPayslipDetail.hours') }}</p>
              </div>
              <button
                v-if="payslip.earnings.overtimeDetails && payslip.earnings.overtimeDetails.length > 0"
                @click="showOvertimeDetails = !showOvertimeDetails"
                class="ml-3 p-1 rounded-lg text-slate-500 hover:text-slate-300 hover:bg-white/5 transition-colors">
                <svg class="w-5 h-5 transform transition-transform duration-200"
                     :class="{ 'rotate-180': showOvertimeDetails }"
                     fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7-7-7 7" />
                </svg>
              </button>
            </div>
            <div v-if="showOvertimeDetails" class="mt-4 pt-4 space-y-2" style="border-top: 1px solid rgba(52,211,153,0.18)">
              <div v-for="(session, index) in payslip.earnings.overtimeDetails" :key="index"
                   class="rounded-lg px-3 py-2.5 text-xs"
                   style="background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.06)">
                <div class="flex justify-between">
                  <span class="font-medium text-slate-200">{{ formatDate(session.date) }}</span>
                  <span class="text-emerald-400 font-semibold tabular-nums">+{{ formatCurrency(session.amount) }}</span>
                </div>
                <div class="text-slate-500 mt-1">
                  {{ session.hours }} hours × {{ formatCurrency(session.rate) }}/hr × {{ session.multiplier }}x
                </div>
                <div v-if="session.approvedBy" class="text-slate-600 mt-1">Approved by {{ session.approvedBy }}</div>
              </div>
            </div>
          </div>

          <!-- Manual adjustment -->
          <div v-if="payslip.earnings.manualAdjustment !== 0"
               class="flex justify-between items-center py-2.5" style="border-bottom: 1px solid rgba(255,255,255,0.06)">
            <div>
              <span class="text-sm text-slate-400">{{ t('empPayslipDetail.manualAdjustment') }}</span>
              <p v-if="payslip.earnings.manualAdjustmentReason" class="text-xs text-slate-600">
                {{ payslip.earnings.manualAdjustmentReason }}
              </p>
            </div>
            <span :class="payslip.earnings.manualAdjustment > 0 ? 'text-emerald-400' : 'text-red-400'"
                  class="text-base font-semibold tabular-nums">
              {{ payslip.earnings.manualAdjustment > 0 ? '+' : '' }}{{ formatCurrency(payslip.earnings.manualAdjustment) }}
            </span>
          </div>
        </div>
      </div>

      <!-- Deductions -->
      <div v-if="hasDeductions" class="ep-card p-5 sm:p-6">
        <h2 class="ep-section-title mb-4">
          <span class="ep-stat-icon ep-stat-rose !w-8 !h-8 !rounded-lg">
            <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M20 12H4" />
            </svg>
          </span>
          {{ t('empPayslipDetail.deductions') }}
        </h2>
        <div class="space-y-3">
          <DeductionItem
            v-if="payslip.deductions.absentDays > 0"
            :title="t('empPayslipDetail.absentDays')"
            :amount="payslip.deductions.absentDays"
            :details="formatAbsentDaysDetails(payslip.deductions.absentDaysDetails)"
            type="absent_days"
          />
          <DeductionItem
            v-if="payslip.deductions.lateArrivals > 0"
            :title="t('empPayslipDetail.lateArrivals')"
            :amount="payslip.deductions.lateArrivals"
            :details="formatLateArrivalsDetails(payslip.deductions.lateArrivalsDetails)"
            type="late_arrivals"
          />
          <DeductionItem
            v-if="payslip.deductions.shortLeave > 0"
            :title="t('empPayslipDetail.shortLeave')"
            :amount="payslip.deductions.shortLeave"
            :details="formatShortLeaveDetails(payslip.deductions.shortLeaveDetails)"
            type="short_leave"
          />
          <DeductionItem
            v-if="payslip.deductions.earlyDeparture > 0"
            :title="t('empPayslipDetail.earlyDeparture')"
            :amount="payslip.deductions.earlyDeparture"
            :details="formatEarlyDepartureDetails(payslip.deductions.earlyDepartureDetails)"
            type="early_departure"
          />
        </div>
      </div>

      <!-- Attendance Statistics -->
      <div class="ep-card p-5 sm:p-6">
        <h2 class="ep-section-title mb-4">{{ t('empPayslipDetail.attendanceStats') }}</h2>
        <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div class="rounded-xl p-4" style="background: rgba(99,102,241,0.08); border: 1px solid rgba(99,102,241,0.2)">
            <p class="text-xs font-medium text-indigo-300">{{ t('empPayslipDetail.workingDays') }}</p>
            <p class="text-2xl font-bold text-slate-100 tabular-nums mt-1">{{ payslip.attendanceStats.workingDaysInPeriod }}</p>
          </div>
          <div class="rounded-xl p-4" style="background: rgba(52,211,153,0.07); border: 1px solid rgba(52,211,153,0.2)">
            <p class="text-xs font-medium text-emerald-300">{{ t('empPayslipDetail.daysAttended') }}</p>
            <p class="text-2xl font-bold text-slate-100 tabular-nums mt-1">{{ payslip.attendanceStats.daysAttended }}</p>
          </div>
          <div class="rounded-xl p-4" style="background: rgba(248,113,113,0.07); border: 1px solid rgba(248,113,113,0.2)">
            <p class="text-xs font-medium text-red-300">{{ t('empPayslipDetail.daysAbsent') }}</p>
            <p class="text-2xl font-bold text-slate-100 tabular-nums mt-1">{{ payslip.attendanceStats.daysAbsent }}</p>
          </div>
          <div class="rounded-xl p-4" style="background: rgba(251,191,36,0.07); border: 1px solid rgba(251,191,36,0.2)">
            <p class="text-xs font-medium text-amber-300">{{ t('empPayslipDetail.lateArrivals') }}</p>
            <p class="text-2xl font-bold text-slate-100 tabular-nums mt-1">{{ payslip.attendanceStats.lateArrivals }}</p>
          </div>
          <div class="rounded-xl p-4" style="background: rgba(167,139,250,0.08); border: 1px solid rgba(167,139,250,0.2)">
            <p class="text-xs font-medium text-violet-300">{{ t('empPayslipDetail.onTimeArrivals') }}</p>
            <p class="text-2xl font-bold text-slate-100 tabular-nums mt-1">{{ payslip.attendanceStats.onTimeArrivals }}</p>
          </div>
          <div class="rounded-xl p-4" style="background: rgba(56,189,248,0.07); border: 1px solid rgba(56,189,248,0.2)">
            <p class="text-xs font-medium text-sky-300">{{ t('empPayslipDetail.hoursWorked') }}</p>
            <p class="text-2xl font-bold text-slate-100 tabular-nums mt-1">{{ payslip.attendanceStats.totalHoursWorked }}</p>
          </div>
          <div class="rounded-xl p-4" style="background: rgba(251,113,133,0.07); border: 1px solid rgba(251,113,133,0.2)">
            <p class="text-xs font-medium text-rose-300">{{ t('empPayslipDetail.expectedHours') }}</p>
            <p class="text-2xl font-bold text-slate-100 tabular-nums mt-1">{{ payslip.attendanceStats.expectedHours }}</p>
          </div>
          <div class="rounded-xl p-4" style="background: rgba(45,212,191,0.07); border: 1px solid rgba(45,212,191,0.2)">
            <p class="text-xs font-medium text-teal-300">{{ t('empPayslipDetail.overtimeHours') }}</p>
            <p class="text-2xl font-bold text-slate-100 tabular-nums mt-1">{{ payslip.attendanceStats.overtimeHours }}</p>
          </div>
        </div>
      </div>

      <!-- Leave Balances -->
      <div v-if="payslip.leaveBalances && payslip.leaveBalances.length > 0" class="ep-card p-5 sm:p-6">
        <h2 class="ep-section-title mb-4">{{ t('empPayslipDetail.leaveBalances') }}</h2>
        <div class="space-y-3">
          <div v-for="(balance, index) in payslip.leaveBalances" :key="index"
               class="rounded-xl p-4"
               style="background: rgba(99,102,241,0.06); border: 1px solid rgba(99,102,241,0.18)">
            <div class="flex justify-between items-center mb-2.5">
              <div>
                <h3 class="text-sm font-semibold text-slate-200">{{ balance.bankName }}</h3>
                <p class="text-xs text-slate-600">{{ balance.bankCode }}</p>
              </div>
              <div class="text-right">
                <p class="text-lg font-bold text-indigo-300 tabular-nums">{{ balance.remaining }} {{ t('empPayslipDetail.days') }}</p>
                <p class="text-xs text-slate-500">{{ balance.percentage }}{{ t('empPayslipDetail.percentRemaining') }}</p>
              </div>
            </div>
            <div class="ep-progress">
              <div class="ep-progress-bar" :style="{ width: Math.min(balance.percentage, 100) + '%' }"></div>
            </div>
            <div class="mt-2.5 grid grid-cols-3 gap-2 text-xs">
              <div><span class="text-slate-600">{{ t('empPayslipDetail.allocated') }}</span> <span class="font-medium text-slate-300">{{ balance.allocated }}</span></div>
              <div><span class="text-slate-600">{{ t('empPayslipDetail.used') }}</span> <span class="font-medium text-slate-300">{{ balance.used }}</span></div>
              <div><span class="text-slate-600">{{ t('empPayslipDetail.pending') }}</span> <span class="font-medium text-slate-300">{{ balance.pending }}</span></div>
            </div>
          </div>
        </div>
      </div>

      <!-- Payment Information -->
      <div class="ep-card p-5 sm:p-6">
        <h2 class="ep-section-title mb-4">{{ t('empPayslipDetail.paymentInfo') }}</h2>
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <p class="ep-label">{{ t('empPayslipDetail.paymentMethod') }}</p>
            <p class="text-sm font-medium text-slate-200">{{ payslip.paymentInfo.method }}</p>
          </div>
          <div>
            <p class="ep-label">{{ t('empPayslipDetail.bankAccount') }}</p>
            <p class="text-sm font-medium text-slate-200">{{ payslip.paymentInfo.bankAccount }}</p>
          </div>
          <div v-if="payslip.paymentInfo.paymentDate">
            <p class="ep-label">{{ t('empPayslipDetail.paymentDate') }}</p>
            <p class="text-sm font-medium text-slate-200">{{ formatDate(payslip.paymentInfo.paymentDate) }}</p>
          </div>
          <div v-if="payslip.paymentInfo.paidBy">
            <p class="ep-label">{{ t('empPayslipDetail.paidBy') }}</p>
            <p class="text-sm font-medium text-slate-200">{{ payslip.paymentInfo.paidBy }}</p>
          </div>
        </div>
      </div>

      <!-- Audit Trail -->
      <div class="rounded-2xl p-4"
           style="background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.05)">
        <h3 class="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">{{ t('empPayslipDetail.auditTrail') }}</h3>
        <div class="text-xs text-slate-500 space-y-1">
          <div v-if="payslip.generatedAt">
            <span class="font-medium text-slate-400">{{ t('empPayslipDetail.generated') }}</span> {{ formatDate(payslip.generatedAt) }}
            <span v-if="payslip.generatedBy">{{ t('empPayslipDetail.by') }} {{ payslip.generatedBy }}</span>
          </div>
          <div v-if="payslip.finalizedAt">
            <span class="font-medium text-slate-400">{{ t('empPayslipDetail.finalized') }}</span> {{ formatDate(payslip.finalizedAt) }}
            <span v-if="payslip.finalizedBy">{{ t('empPayslipDetail.by') }} {{ payslip.finalizedBy }}</span>
          </div>
          <div v-if="payslip.notes">
            <span class="font-medium text-slate-400">{{ t('empPayslipDetail.notes') }}</span> {{ payslip.notes }}
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { api } from '@/services/api';
const { t } = useI18n();
import { useCurrency } from '@/composables/useCurrency';
import DeductionItem from '@/components/payroll/DeductionItem.vue';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const route = useRoute();
const router = useRouter();
const { formatCurrency, formatCurrencyForPDF, loadCurrency } = useCurrency();

const payslip = ref(null);
const loading = ref(true);
const error = ref(null);
const showOvertimeDetails = ref(false);
const isDownloadingPDF = ref(false);
const isDownloadingReceipt = ref(false);

const monthNames = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

onMounted(async () => {
  loadCurrency();
  await fetchPayslip();
});

async function fetchPayslip() {
  try {
    loading.value = true;
    error.value = null;

    const response = await api.get(`/employee/payslips/${route.params.id}`);

    if (response.data?.success) {
      payslip.value = response.data.data;
    } else {
      throw new Error(response.data?.error || 'Failed to fetch payslip');
    }
  } catch (err) {
    console.error('Error fetching payslip:', err);
    error.value = err.response?.data?.error || err.message || 'Failed to load payslip';
  } finally {
    loading.value = false;
  }
}

const hasDeductions = computed(() => {
  if (!payslip.value) return false;
  return payslip.value.deductions.totalDeductions > 0;
});

function formatAbsentDaysDetails(details) {
  if (!details || details.length === 0) return null;

  const hourlyRate = details[0]?.rate || 0;
  return {
    dates: details.map(d => d.date),
    hourly_rate: hourlyRate,
    total_days: details.length
  };
}

function formatLateArrivalsDetails(details) {
  if (!details || details.length === 0) return null;

  return {
    incidents: details.map(d => ({
      date: d.date,
      minutes: Math.round(d.hoursOrMinutes),
      deduction: d.amount
    })),
    total_incidents: details.length
  };
}

function formatTime(datetimeValue) {
  if (!datetimeValue) return 'N/A';
  const date = new Date(datetimeValue);
  if (isNaN(date.getTime())) return 'N/A';
  return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
}

function formatShortLeaveDetails(details) {
  if (!details || details.length === 0) return null;

  const hourlyRate = details[0]?.rate || 0;
  const totalHours = details.reduce((sum, d) => sum + parseFloat(d.hoursOrMinutes), 0);

  return {
    sessions: details.map(d => ({
      date: d.date,
      hours: parseFloat(d.hoursOrMinutes),
      deduction: d.amount,
      start_time: formatTime(d.startTime),
      end_time: formatTime(d.endTime)
    })),
    total_hours: totalHours,
    hourly_rate: hourlyRate
  };
}

function formatEarlyDepartureDetails(details) {
  if (!details || details.length === 0) return null;

  return {
    incidents: details.map(d => ({
      date: d.date,
      minutes: Math.round(d.hoursOrMinutes),
      deduction: d.amount
    })),
    total_incidents: details.length
  };
}

function getMonthName(month) {
  return monthNames[month - 1] || 'Unknown';
}

function formatDate(dateString) {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}

function getStatusText(status) {
  const statusMap = {
    draft: 'Draft',
    finalized: 'Finalized',
    paid: 'Paid'
  };
  return statusMap[status] || status;
}

function getStatusClass(status) {
  const classMap = {
    draft: 'ep-badge ep-badge-neutral',
    finalized: 'ep-badge ep-badge-info',
    paid: 'ep-badge ep-badge-success'
  };
  return classMap[status] || 'ep-badge ep-badge-neutral';
}

async function fetchLogo(url) {
  if (!url) return null;
  try {
    const resp = await fetch(url);
    const blob = await resp.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  } catch (_) {
    return null;
  }
}

async function downloadPDF() {
  if (!payslip.value) return;
  isDownloadingPDF.value = true;
  try {
    const p = payslip.value;
    // jsPDF built-in fonts don't support non-Latin-1 glyphs (₪, €, ₹ …)
    // so use the ISO code prefix format (e.g. "ILS 1,234.56") inside the PDF
    const formatCurrency = formatCurrencyForPDF;
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const W = doc.internal.pageSize.getWidth();
    const H = doc.internal.pageSize.getHeight();
    const margin = 14;
    const companyName    = p.companyName    || 'ExoTrack';
    const companyAddress = p.companyAddress || '';
    const companyPhone   = p.companyPhone   || '';
    const companyWebsite = p.companyWebsite || '';
    const logoDataUrl    = await fetchLogo(p.companyLogoUrl);

    // ── Header band ──────────────────────────────────────────────────────
    const HEADER_H = logoDataUrl ? 46 : 38;
    doc.setFillColor(17, 24, 39);
    doc.rect(0, 0, W, HEADER_H, 'F');
    doc.setFillColor(37, 99, 235);
    doc.rect(0, HEADER_H, W, 2.5, 'F');

    const LOGO_H = 22, LOGO_W = 44;
    let textStartX = margin;
    if (logoDataUrl) {
      try {
        doc.addImage(logoDataUrl, margin, (HEADER_H - LOGO_H) / 2, LOGO_W, LOGO_H, '', 'FAST');
        textStartX = margin + LOGO_W + 7;
      } catch (_) {}
    }

    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(logoDataUrl ? 17 : 23);
    doc.text(companyName.toUpperCase(), textStartX, logoDataUrl ? 15 : 16);

    if (logoDataUrl) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7.5);
      doc.setTextColor(147, 197, 253);
      let subY = 22;
      if (companyAddress) { doc.text(companyAddress, textStartX, subY); subY += 5.5; }
      const contact = [companyPhone, companyWebsite].filter(Boolean).join('  •  ');
      if (contact) doc.text(contact, textStartX, subY);
    }

    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.text('PAYSLIP', W - margin, 12, { align: 'right' });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.text(`${getMonthName(p.period.month)} ${p.period.year}`, W - margin, 20, { align: 'right' });
    doc.setFontSize(8.5);
    doc.setTextColor(147, 197, 253);
    doc.text(`${formatDate(p.period.startDate)} – ${formatDate(p.period.endDate)}`, W - margin, 27, { align: 'right' });

    const statusLabel = getStatusText(p.status).toUpperCase();
    const sColors = { DRAFT: [75,85,99], FINALIZED: [37,99,235], PAID: [5,150,105] };
    const [sr, sg, sb] = sColors[statusLabel] || [75,85,99];
    doc.setFillColor(sr, sg, sb);
    doc.roundedRect(W - margin - 26, HEADER_H - 11, 26, 7.5, 2, 2, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.text(statusLabel, W - margin - 13, HEADER_H - 6, { align: 'center' });

    // ── Employee info block ──────────────────────────────────────────────
    let y = HEADER_H + 9;
    const empBlockH = p.employee.position ? 33 : 27;
    doc.setFillColor(248, 250, 252);
    doc.rect(margin, y, W - margin * 2, empBlockH, 'F');
    doc.setDrawColor(226, 232, 240);
    doc.rect(margin, y, W - margin * 2, empBlockH, 'S');

    const col1x = margin + 4, col2x = W / 2 + 4;
    doc.setFont('helvetica', 'bold'); doc.setFontSize(7); doc.setTextColor(30, 58, 138);
    doc.text('EMPLOYEE INFORMATION', col1x, y + 5.5);

    doc.setFontSize(9);
    const empFields = [
      { label: 'Name:', value: p.employee.name || '—', x: col1x, labelW: 18 },
      { label: 'Code:', value: p.employee.code || '—', x: col2x, labelW: 18 },
      { label: 'Department:', value: p.employee.department || '—', x: col1x, labelW: 26 },
      { label: 'Period:', value: `${formatDate(p.period.startDate)} – ${formatDate(p.period.endDate)}`, x: col2x, labelW: 18 },
    ];
    if (p.employee.position) {
      empFields.push({ label: 'Position:', value: p.employee.position, x: col1x, labelW: 20 });
    }

    const empRows2 = [empFields.slice(0, 2), empFields.slice(2, 4)];
    if (p.employee.position) empRows2.push([empFields[4]]);

    empRows2.forEach((row, ri) => {
      const rowY = y + 12 + ri * 7;
      row.forEach(({ label, value, x, labelW }) => {
        doc.setFont('helvetica', 'normal'); doc.setTextColor(100, 116, 139);
        doc.text(label, x, rowY);
        doc.setFont('helvetica', 'bold'); doc.setTextColor(15, 23, 42);
        doc.text(value, x + labelW, rowY);
      });
    });

    y += empBlockH + 8;

    // ── Earnings ─────────────────────────────────────────────────────────
    doc.setFont('helvetica', 'bold'); doc.setFontSize(8.5); doc.setTextColor(21, 128, 61);
    doc.text('EARNINGS', margin, y); y += 3;

    const earningsRows = [['Base Salary', `${formatCurrency(p.earnings.hourlyRate)}/hr × ${p.earnings.workingHoursPerMonth} hrs`, formatCurrency(p.earnings.baseSalary)]];
    if (p.earnings.overtimePay > 0)
      earningsRows.push([`Overtime Pay`, `${p.earnings.overtimeHours} hrs`, formatCurrency(p.earnings.overtimePay)]);
    if (p.earnings.manualAdjustment !== 0)
      earningsRows.push(['Manual Adjustment', p.earnings.manualAdjustmentReason || '', formatCurrency(p.earnings.manualAdjustment)]);

    autoTable(doc, {
      startY: y,
      head: [['Description', 'Details', 'Amount']],
      body: earningsRows,
      foot: [['', 'Gross Pay', formatCurrency(p.earnings.grossPay)]],
      theme: 'plain',
      styles: { fontSize: 9, cellPadding: 3, textColor: [31, 41, 55] },
      headStyles: { fillColor: [240, 253, 244], textColor: [21, 128, 61], fontStyle: 'bold', fontSize: 8 },
      footStyles: { fillColor: [220, 252, 231], textColor: [21, 128, 61], fontStyle: 'bold', fontSize: 9 },
      columnStyles: { 1: { textColor: [107, 114, 128], fontSize: 8 }, 2: { halign: 'right', fontStyle: 'bold' } },
      margin: { left: margin, right: margin },
      tableWidth: W - margin * 2,
    });
    y = doc.lastAutoTable.finalY + 6;

    // ── Deductions ───────────────────────────────────────────────────────
    if (p.deductions.totalDeductions > 0) {
      doc.setFont('helvetica', 'bold'); doc.setFontSize(8.5); doc.setTextColor(185, 28, 28);
      doc.text('DEDUCTIONS', margin, y); y += 3;

      const deductRows = [];
      if (p.deductions.absentDays > 0)
        deductRows.push([`Absent Days`, `${p.deductions.absentDaysDetails?.length || 0} day(s)`, formatCurrency(p.deductions.absentDays)]);
      if (p.deductions.lateArrivals > 0)
        deductRows.push([`Late Arrivals`, `${p.deductions.lateArrivalsDetails?.length || 0} incident(s)`, formatCurrency(p.deductions.lateArrivals)]);
      if (p.deductions.shortLeave > 0)
        deductRows.push([`Short Leave`, `${p.deductions.shortLeaveDetails?.length || 0} session(s)`, formatCurrency(p.deductions.shortLeave)]);
      if (p.deductions.earlyDeparture > 0)
        deductRows.push([`Early Departure`, `${p.deductions.earlyDepartureDetails?.length || 0} incident(s)`, formatCurrency(p.deductions.earlyDeparture)]);

      autoTable(doc, {
        startY: y,
        head: [['Description', 'Count', 'Amount']],
        body: deductRows,
        foot: [['', 'Total Deductions', formatCurrency(p.deductions.totalDeductions)]],
        theme: 'plain',
        styles: { fontSize: 9, cellPadding: 3, textColor: [31, 41, 55] },
        headStyles: { fillColor: [255, 241, 242], textColor: [185, 28, 28], fontStyle: 'bold', fontSize: 8 },
        footStyles: { fillColor: [254, 226, 226], textColor: [185, 28, 28], fontStyle: 'bold', fontSize: 9 },
        columnStyles: { 1: { textColor: [107, 114, 128], fontSize: 8 }, 2: { halign: 'right', fontStyle: 'bold' } },
        margin: { left: margin, right: margin },
        tableWidth: W - margin * 2,
      });
      y = doc.lastAutoTable.finalY + 6;
    }

    // ── Net Pay bar ───────────────────────────────────────────────────────
    doc.setFillColor(17, 24, 39);
    doc.roundedRect(margin, y, W - margin * 2, 16, 2.5, 2.5, 'F');
    doc.setFillColor(37, 99, 235);
    doc.roundedRect(margin, y, 4, 16, 2.5, 0, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold'); doc.setFontSize(10.5);
    doc.text('NET PAY', margin + 9, y + 10.5);
    doc.setFontSize(14);
    doc.text(formatCurrency(p.summary.netPay), W - margin - 5, y + 11, { align: 'right' });
    y += 23;

    // ── Attendance + Leave side by side ──────────────────────────────────
    const halfW = (W - margin * 2 - 6) / 2;
    doc.setFont('helvetica', 'bold'); doc.setFontSize(8.5); doc.setTextColor(30, 58, 138);
    doc.text('ATTENDANCE', margin, y);
    if (p.leaveBalances && p.leaveBalances.length > 0)
      doc.text('LEAVE BALANCES', margin + halfW + 6, y);
    y += 3;

    autoTable(doc, {
      startY: y,
      head: [['Metric', 'Value']],
      body: [
        ['Working Days', String(p.attendanceStats.workingDaysInPeriod)],
        ['Days Attended', String(p.attendanceStats.daysAttended)],
        ['Days Absent', String(p.attendanceStats.daysAbsent)],
        ['Late Arrivals', String(p.attendanceStats.lateArrivals)],
        ['Hours Worked', `${p.attendanceStats.totalHoursWorked} h`],
        ['Overtime Hours', `${p.attendanceStats.overtimeHours} h`],
      ],
      theme: 'grid',
      styles: { fontSize: 8, cellPadding: 2.5, textColor: [31, 41, 55] },
      headStyles: { fillColor: [219, 234, 254], textColor: [30, 64, 175], fontStyle: 'bold', fontSize: 8 },
      columnStyles: { 1: { halign: 'right', fontStyle: 'bold' } },
      margin: { left: margin },
      tableWidth: halfW,
    });

    const attendFinalY = doc.lastAutoTable.finalY;

    if (p.leaveBalances && p.leaveBalances.length > 0) {
      autoTable(doc, {
        startY: y,
        head: [['Leave Type', 'Remaining', 'Total']],
        body: p.leaveBalances.map(b => [b.bankName, `${b.remaining} d  (${b.percentage}%)`, `${b.allocated} d`]),
        theme: 'grid',
        styles: { fontSize: 8, cellPadding: 2.5, textColor: [31, 41, 55] },
        headStyles: { fillColor: [243, 232, 255], textColor: [109, 40, 217], fontStyle: 'bold', fontSize: 8 },
        columnStyles: { 1: { halign: 'right', fontStyle: 'bold', textColor: [109, 40, 217] }, 2: { halign: 'right' } },
        margin: { left: margin + halfW + 6 },
        tableWidth: halfW,
      });
    }

    y = Math.max(attendFinalY, doc.lastAutoTable.finalY) + 8;

    // ── Payment info ──────────────────────────────────────────────────────
    if (p.paymentInfo.status === 'paid' || p.paymentInfo.paymentDate || p.paymentInfo.paidBy) {
      doc.setFillColor(240, 253, 244);
      doc.setDrawColor(167, 243, 208);
      doc.roundedRect(margin, y, W - margin * 2, 14, 2, 2, 'FD');
      doc.setFont('helvetica', 'bold'); doc.setFontSize(7.5); doc.setTextColor(21, 128, 61);
      doc.text('PAYMENT INFORMATION', margin + 4, y + 5);
      doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(55, 65, 81);
      const payParts = [];
      if (p.paymentInfo.method) payParts.push(`Method: ${p.paymentInfo.method}`);
      if (p.paymentInfo.paymentDate) payParts.push(`Date: ${formatDate(p.paymentInfo.paymentDate)}`);
      if (p.paymentInfo.paidBy) payParts.push(`By: ${p.paymentInfo.paidBy}`);
      doc.text(payParts.join('   •   '), margin + 4, y + 11);
      y += 22;
    }

    // ── Notes ─────────────────────────────────────────────────────────────
    if (p.notes) {
      doc.setFont('helvetica', 'bold'); doc.setFontSize(8); doc.setTextColor(55, 65, 81);
      doc.text('NOTES', margin, y); y += 4;
      doc.setFont('helvetica', 'normal'); doc.setFontSize(8);
      const lines = doc.splitTextToSize(p.notes, W - margin * 2);
      doc.text(lines, margin, y);
      y += lines.length * 4 + 6;
    }

    // ── Footer band ───────────────────────────────────────────────────────
    doc.setFillColor(248, 250, 252);
    doc.setDrawColor(226, 232, 240);
    doc.rect(0, H - 14, W, 14, 'FD');
    doc.setTextColor(107, 114, 128);
    doc.setFont('helvetica', 'italic'); doc.setFontSize(7);
    doc.text(`Generated ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`, margin, H - 5);
    const footerContact = [companyPhone, companyWebsite].filter(Boolean).join('  •  ');
    if (footerContact) {
      doc.setFont('helvetica', 'normal');
      doc.text(footerContact, W / 2, H - 5, { align: 'center' });
    }
    doc.setFont('helvetica', 'normal');
    doc.text(`${companyName} · Payroll System`, W - margin, H - 5, { align: 'right' });
    if (!logoDataUrl && companyAddress) {
      doc.setFontSize(6.5);
      doc.text(companyAddress, W / 2, H - 1, { align: 'center' });
    }

    const code = p.employee.code || p.employee.id || 'emp';
    doc.save(`Payslip_${code}_${p.period.year}_${String(p.period.month).padStart(2, '0')}.pdf`);
  } finally {
    isDownloadingPDF.value = false;
  }
}

async function downloadReceipt() {
  if (!payslip.value) return;
  isDownloadingReceipt.value = true;
  try {
    const p = payslip.value;
    // Same reason as downloadPDF — use ISO code prefix for PDF-safe output
    const formatCurrency = formatCurrencyForPDF;
    const companyName    = p.companyName    || 'ExoTrack';
    const companyAddress = p.companyAddress || '';
    const companyPhone   = p.companyPhone   || '';
    const companyWebsite = p.companyWebsite || '';
    const logoDataUrl    = await fetchLogo(p.companyLogoUrl);

    // Dynamic height estimation
    const RW = 80;
    let receiptH = 168;
    if (logoDataUrl) receiptH += 20;
    if (companyAddress) receiptH += 5;
    if (companyPhone || companyWebsite) receiptH += 5;
    if (p.earnings.overtimePay > 0) receiptH += 6;
    if (p.earnings.manualAdjustment !== 0) receiptH += 6;
    if (p.deductions.totalDeductions > 0) {
      receiptH += 16;
      if (p.deductions.absentDays > 0)    receiptH += 6;
      if (p.deductions.lateArrivals > 0)  receiptH += 6;
      if (p.deductions.shortLeave > 0)    receiptH += 6;
      if (p.deductions.earlyDeparture > 0) receiptH += 6;
    }
    if (p.leaveBalances && p.leaveBalances.length > 0) receiptH += 10 + p.leaveBalances.length * 6;
    if (p.notes) receiptH += 22;
    receiptH += 12; // safety

    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: [RW, receiptH] });
    const margin = 3;
    const cx = RW / 2;
    let y = 5;

    // Helpers ──────────────────────────────────────────────────────────────
    const divider = (heavy = false) => {
      doc.setDrawColor(heavy ? 30 : 200, heavy ? 30 : 200, heavy ? 30 : 200);
      doc.setLineWidth(heavy ? 0.5 : 0.15);
      if (heavy) {
        doc.line(margin, y, RW - margin, y);
        doc.line(margin, y + 1, RW - margin, y + 1);
        y += 5;
      } else {
        doc.line(margin, y, RW - margin, y);
        y += 4;
      }
    };

    const cText = (text, size, bold, color) => {
      doc.setFont('courier', bold ? 'bold' : 'normal');
      doc.setFontSize(size);
      doc.setTextColor(...color);
      doc.text(text, cx, y, { align: 'center' });
      y += size * 0.41 + 1.5;
    };

    const rRow = (label, value, lBold, vBold, color) => {
      const c = color || [31, 41, 55];
      doc.setFont('courier', lBold ? 'bold' : 'normal');
      doc.setFontSize(8);
      doc.setTextColor(...c);
      doc.text(label, margin, y);
      doc.setFont('courier', vBold ? 'bold' : 'normal');
      doc.text(value, RW - margin, y, { align: 'right' });
      y += 5.8;
    };

    // ── Logo ───────────────────────────────────────────────────────────────
    if (logoDataUrl) {
      try {
        const LW = 22, LH = 11;
        doc.addImage(logoDataUrl, (RW - LW) / 2, y, LW, LH, '', 'FAST');
        y += LH + 4;
      } catch (_) {}
    }

    // ── Header ─────────────────────────────────────────────────────────────
    cText(companyName.toUpperCase(), 10, true, [17, 24, 39]);
    if (companyAddress) cText(companyAddress, 7, false, [107, 114, 128]);
    const contact = [companyPhone, companyWebsite].filter(Boolean).join(' | ');
    if (contact) cText(contact, 7, false, [107, 114, 128]);
    y += 2;
    divider(false);

    cText('PAYROLL RECEIPT', 9, true, [30, 64, 175]);
    cText(`${getMonthName(p.period.month)} ${p.period.year}`, 8, false, [55, 65, 81]);
    y += 1;
    divider(false);

    // ── Employee ───────────────────────────────────────────────────────────
    const empLabel = (lbl, val) => {
      doc.setFont('courier', 'normal'); doc.setFontSize(8); doc.setTextColor(100, 116, 139);
      doc.text(lbl, margin, y);
      doc.setFont('courier', 'bold'); doc.setTextColor(15, 23, 42);
      doc.text(val, margin + 22, y);
      y += 5.5;
    };
    empLabel('Name:', p.employee.name || '—');
    empLabel('Code:', p.employee.code || '—');
    empLabel('Department:', p.employee.department || '—');
    if (p.employee.position) empLabel('Position:', p.employee.position);
    y += 1;
    divider(false);

    // ── Earnings ───────────────────────────────────────────────────────────
    doc.setFont('courier', 'bold'); doc.setFontSize(8); doc.setTextColor(21, 128, 61);
    doc.text('EARNINGS', margin, y); y += 5.5;
    doc.setTextColor(31, 41, 55);
    rRow('Base Salary', formatCurrency(p.earnings.baseSalary));
    if (p.earnings.overtimePay > 0)
      rRow(`Overtime (${p.earnings.overtimeHours} h)`, formatCurrency(p.earnings.overtimePay));
    if (p.earnings.manualAdjustment !== 0)
      rRow('Manual Adj.', formatCurrency(p.earnings.manualAdjustment));

    doc.setDrawColor(200, 200, 200); doc.setLineWidth(0.1);
    doc.line(margin, y - 1, RW - margin, y - 1); y += 1;
    rRow('GROSS PAY', formatCurrency(p.earnings.grossPay), true, true, [21, 128, 61]);

    // ── Deductions ─────────────────────────────────────────────────────────
    if (p.deductions.totalDeductions > 0) {
      y += 1; divider(false);
      doc.setFont('courier', 'bold'); doc.setFontSize(8); doc.setTextColor(185, 28, 28);
      doc.text('DEDUCTIONS', margin, y); y += 5.5;
      doc.setTextColor(31, 41, 55);
      if (p.deductions.absentDays > 0)
        rRow(`Absent (${p.deductions.absentDaysDetails?.length || 0}d)`, formatCurrency(p.deductions.absentDays));
      if (p.deductions.lateArrivals > 0)
        rRow(`Late Arrivals (${p.deductions.lateArrivalsDetails?.length || 0})`, formatCurrency(p.deductions.lateArrivals));
      if (p.deductions.shortLeave > 0)
        rRow(`Short Leave (${p.deductions.shortLeaveDetails?.length || 0})`, formatCurrency(p.deductions.shortLeave));
      if (p.deductions.earlyDeparture > 0)
        rRow(`Early Dep. (${p.deductions.earlyDepartureDetails?.length || 0})`, formatCurrency(p.deductions.earlyDeparture));

      doc.setDrawColor(200, 200, 200); doc.setLineWidth(0.1);
      doc.line(margin, y - 1, RW - margin, y - 1); y += 1;
      rRow('TOTAL DED.', formatCurrency(p.deductions.totalDeductions), true, true, [185, 28, 28]);
    }

    // ── Net Pay ────────────────────────────────────────────────────────────
    y += 3; divider(true);
    cText('NET PAY', 8, true, [17, 24, 39]);
    doc.setFont('courier', 'bold'); doc.setFontSize(18); doc.setTextColor(17, 24, 39);
    doc.text(formatCurrency(p.summary.netPay), cx, y, { align: 'center' });
    y += 12;
    divider(true); y += 1;

    // ── Attendance ─────────────────────────────────────────────────────────
    doc.setFont('courier', 'bold'); doc.setFontSize(8); doc.setTextColor(71, 85, 105);
    doc.text('ATTENDANCE', margin, y); y += 5.5;
    doc.setTextColor(55, 65, 81);
    [
      ['Working Days',   String(p.attendanceStats.workingDaysInPeriod)],
      ['Days Attended',  String(p.attendanceStats.daysAttended)],
      ['Days Absent',    String(p.attendanceStats.daysAbsent)],
      ['Late Arrivals',  String(p.attendanceStats.lateArrivals)],
      ['Hours Worked',   `${p.attendanceStats.totalHoursWorked} h`],
      ['Overtime Hrs',   `${p.attendanceStats.overtimeHours} h`],
    ].forEach(([l, v]) => rRow(l, v));

    // ── Leave balances ─────────────────────────────────────────────────────
    if (p.leaveBalances && p.leaveBalances.length > 0) {
      y += 1; divider(false);
      doc.setFont('courier', 'bold'); doc.setFontSize(8); doc.setTextColor(109, 40, 217);
      doc.text('LEAVE BALANCES', margin, y); y += 5.5;
      doc.setTextColor(55, 65, 81);
      p.leaveBalances.forEach(b => rRow(b.bankName, `${b.remaining}/${b.allocated} d`));
    }

    // ── Notes ──────────────────────────────────────────────────────────────
    if (p.notes) {
      y += 1; divider(false);
      doc.setFont('courier', 'bold'); doc.setFontSize(7.5); doc.setTextColor(55, 65, 81);
      doc.text('NOTES', margin, y); y += 4.5;
      doc.setFont('courier', 'normal');
      const noteLines = doc.splitTextToSize(p.notes, RW - margin * 2);
      doc.text(noteLines, margin, y);
      y += noteLines.length * 4.5;
    }

    // ── Footer ─────────────────────────────────────────────────────────────
    y += 3; divider(false);
    const statusLabel = getStatusText(p.status).toUpperCase();
    const sCol = p.status === 'paid' ? [5, 150, 105] : p.status === 'finalized' ? [37, 99, 235] : [107, 114, 128];
    cText(`STATUS: ${statusLabel}`, 8, true, sCol);
    cText(`Generated: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}`, 7, false, [107, 114, 128]);
    cText('* * *', 8, false, [200, 200, 200]);

    const code = p.employee.code || p.employee.id || 'emp';
    doc.save(`Receipt_${code}_${p.period.year}_${String(p.period.month).padStart(2, '0')}.pdf`);
  } finally {
    isDownloadingReceipt.value = false;
  }
}
</script>
